import Vue from "vue";
import Component from "vue-class-component";
import Router from "vue-router";
import admin from "@/router/admin";
import pages from "@/router/pages";

import LoginPage from "@/custom/page/LoginPage.vue";
import MenuPage from "@/custom/page/MenuPage.vue";
import UserAdministrationPage from "@/custom/page/useradmin/UserAdministrationPage.vue";
import DraftPage from "@/custom/page/draft/DraftPage.vue";
import FileManagerPage from "@/custom/page/filemanager/FileManagerPage.vue";
import { Authority } from "@/shared/security/authority";

import ProjectFilesDetail from "@/custom/page/filemanager/file/ProjectFilesDetail.vue";
import ProjectListPage from "@/custom/page/filemanager/project/ProjectListPage.vue";
import ImportFilePage from "@/custom/page/filemanager/file/ImportFilePage.vue";
import FileListPage from "@/custom/page/filemanager/file/FileListPage.vue";
import Map2DViewer from "@/custom/page/filemanager/map/Map2DViewer.vue";
import Vr360Viewer from "@/custom/page/filemanager/map/Vr360Viewer.vue";
import Model3DViewer from "@/custom/page/filemanager/map/Model3DViewer.vue";
import UserListPage from "@/custom/page/useradmin/user/UserListPage.vue";
import GeodeticViewer from "@/custom/page/geodeticviewer/GeodeticViewer.vue";
import GeodeticProjectViewList from "@/custom/page/geodeticviewer/GeodeticProjectViewList.vue";
import PermissionListPage from "@/custom/page/useradmin/permission/PermissionListPage.vue";
import PermissionGrantPage from "@/custom/page/useradmin/permission/PermissionGrantPage.vue";
import VideoM3u8Demo from "@/custom/copy/VideoM3u8Demo.vue";

Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteLeave',
  'beforeRouteUpdate', // for vue-router 2.2+
]);

const Home = () => import('@/core/home/home.vue');
const TcgPage = () => import('@/custom/page/tcgpage.vue');
const Error = () => import('@/core/error/error.vue');

Vue.use(Router);

// prettier-ignore
const router = new Router({
  mode: "history",
  scrollBehavior: (to, from, savedPosition) => {
    if(savedPosition) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({...savedPosition, behavior: 'smooth'});
        }, 600);
      });
    } else {
      return ({x: 0, y: 0});
    }
  },
  routes: [
    {
      path: "/",
      name: "MenuPage",
      component: MenuPage,
      meta: { authorities: [Authority.ADMIN, Authority.USER] }
    },
    {
      path: "/login",
      name: "LoginPage",
      component: LoginPage
    },
    {
      path: "/m3u8",
      name: "VideoM3u8Demo",
      component: VideoM3u8Demo
    },
    {
      path: "/user-administration-page",
      name: "UserAdministrationPage",
      component: UserAdministrationPage,
      meta: { authorities: [Authority.ADMIN] },
      children: [
        // {
        //   path: "role-list",
        //   name: "RoleListPage",
        //   component: RoleListPage,
        //   meta: { authorities: [Authority.ADMIN] }
        // },
        {
          path: "user-list",
          name: "UserListPage",
          component: UserListPage,
          meta: { authorities: [Authority.ADMIN] }
        },
        {
          path: "permission-list",
          name: "PermissionListPage",
          component: PermissionListPage,
          meta: { authorities: [Authority.ADMIN] }
        },
        {
          path: "/permission-grant",
          name: "PermissionGrantPage",
          component: PermissionGrantPage,
          meta: { authorities: [Authority.ADMIN, Authority.USER] }
        },
      ]
    },
    {
      path: "/file-manager-page",
      name: "FileManagerPage",
      component: FileManagerPage,
      meta: { authorities: [Authority.ADMIN, Authority.USER] },
      children: [
        {
          path: "project-list",
          name: "ProjectListPage",
          component: ProjectListPage,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { selectMenuProp: 0 }
        },
        {
          path: "import-file",
          name: "ImportFilePage",
          component: ImportFilePage,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { selectMenuProp: 1 }
        },
        {
          path: "file-list",
          name: "FileListPage",
          component: FileListPage,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { selectMenuProp: 2 }
        },

        {
          path: "file-project/:projectId/files",
          name: "ProjectFilesDetailFileList",
          component: ProjectFilesDetail,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { fileListOpen: true }
        },
        {
          path: "download-file",
          name: "FileListPageDownload",
          component: FileListPage,
          props: { downloadMode: true, selectMenuProp: 3 },
          meta: { authorities: [Authority.ADMIN, Authority.USER] }
        },
        {
          path: "file-project/:projectId/map-view",
          name: "ProjectFilesDetailMapList",
          component: ProjectFilesDetail,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { fileListOpen: false }
        }
      ]
    },
    {
      path: "/draft",
      name: "DraftPage",
      component: DraftPage
    },
    {
      path: "/forbidden",
      name: "Forbidden",
      component: Error,
      meta: { error403: true }
    },
    {
      path: "/not-found",
      name: "NotFound",
      component: Error,
      meta: { error404: true }
    },
    ...admin,
    // entities,
    ...pages,
    {
      path: "/map2d/:map2dId",
      name: "Map2DViewer",
      component: Map2DViewer,
      meta: { authorities: [Authority.ADMIN, Authority.USER] }
    },
    {
      path: "/mobile/map2d/:map2dId",
      name: "Map2DViewer",
      component: Map2DViewer,
      props: { mobileMode: true },
    },
    {
      path: "/vr360/:vr360Id",
      name: "Vr360Viewer",
      component: Vr360Viewer,
      meta: { authorities: [Authority.ADMIN, Authority.USER] }
    },
    {
      path: "/mobile/vr360/:vr360Id",
      name: "Vr360Viewer",
      component: Vr360Viewer,
      props: { mobileMode: true },
    },
    {
      path: "/model3d/:model3dId",
      name: "Model3DViewer",
      component: Model3DViewer,
      meta: { authorities: [Authority.ADMIN, Authority.USER] }
    },
    {
      path: "/mobile/model3d/:model3dId",
      name: "Model3DViewer",
      component: Model3DViewer,
      props: { mobileMode: true },
    },

    {
      path: "/geodetic-viewer",
      name: "GeodeticViewer",
      component: GeodeticViewer,
      meta: { authorities: [Authority.ADMIN, Authority.USER] },
      children: [
        {
          path: "geodetic",
          name: "GeodeticProjectViewList",
          component: GeodeticProjectViewList,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { fileListOpen: false }
        },
        {
          path: "download-file",
          name: "FileListPageDownload",
          component: FileListPage,
          props: { downloadMode: true, selectMenuProp: 3 },
          meta: { authorities: [Authority.ADMIN, Authority.USER] }
        },
        {
          path: ":projectId/map-view",
          name: "ProjectFilesDetailMapListViewer",
          component: ProjectFilesDetail,
          meta: { authorities: [Authority.ADMIN, Authority.USER] },
          props: { fileListOpen: false }
        }
      ]
    }

  ]
});

export default router;
